import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { QRCodeSVG } from 'qrcode.react';
import { FiCopy, FiCheck, FiAlertTriangle, FiImage, FiX, FiDownload, FiArrowDownCircle } from 'react-icons/fi';
import html2canvas from 'html2canvas';
import { useI18n } from '../i18n/useI18n';

export function DepositModal({ open, onClose, walletAddress, walletName, walletType, pushToast }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const [imageCopied, setImageCopied] = useState(false);
  const qrRef = useRef(null);

  // 弹窗打开时锁定背景滚动
  useEffect(() => {
    if (!open) return;

    const scrollY = window.scrollY || 0;
    const prevBodyOverflow = document.body.style.overflow;
    const prevBodyPosition = document.body.style.position;
    const prevBodyTop = document.body.style.top;
    const prevBodyWidth = document.body.style.width;
    const prevHtmlOverflow = document.documentElement.style.overflow;

    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
      document.body.style.position = prevBodyPosition;
      document.body.style.top = prevBodyTop;
      document.body.style.width = prevBodyWidth;
      window.scrollTo(0, scrollY);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  if (!open || !walletAddress) return null;

  const handleCopy = () => {
    navigator.clipboard.writeText(walletAddress);
    setCopied(true);
    pushToast(t('deposit.copyAddress'), 'success');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyImage = async () => {
    if (!qrRef.current) return;
    try {
      const canvas = await html2canvas(qrRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
      });
      
      canvas.toBlob(async (blob) => {
        if (!blob) {
          pushToast(t('deposit.imageGenFailed'), 'error');
          return;
        }
        try {
          const item = new ClipboardItem({ 'image/png': blob });
          await navigator.clipboard.write([item]);
          setImageCopied(true);
          pushToast(t('deposit.qrCopied'), 'success');
          setTimeout(() => setImageCopied(false), 2000);
        } catch (err) {
          console.error('Clipboard write failed:', err);
          pushToast(t('deposit.imageCopyFailed'), 'error');
        }
      }, 'image/png');
    } catch (err) {
      console.error('html2canvas failed:', err);
      pushToast(t('deposit.imageGenError'), 'error');
    }
  };

  const isSafe = walletType?.toLowerCase() === 'safe';

  return createPortal(
    <div
      className="fixed inset-0 z-[210] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl border border-slate-700/60 bg-gradient-to-b from-slate-900/95 to-slate-950/95 backdrop-blur-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-500/15 rounded-xl">
              <FiArrowDownCircle className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">{t('deposit.title')}</h3>
              <p className="text-xs text-slate-500 uppercase tracking-wider">{walletName || 'Wallet'}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Network Warning */}
          <div className="mb-6 bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 flex items-start gap-3">
            <div className="p-1 bg-amber-500/20 rounded-lg shrink-0">
              <FiAlertTriangle className="text-amber-400 w-4 h-4" />
            </div>
            <div className="text-sm text-amber-200/80 leading-relaxed">
              <p className="font-bold text-amber-400 mb-1">{t('deposit.warningTitle')}</p>
              <p className="text-xs">{t('deposit.warningLine1')}</p>
              <p className="text-xs">{t('deposit.warningLine2')}</p>
            </div>
          </div>

          {/* QR Code */}
          <div className="flex flex-col items-center gap-4 mb-6">
            <div className="relative">
              <div ref={qrRef} className="p-4 bg-white rounded-2xl shadow-xl shadow-sky-500/10">
                <QRCodeSVG 
                  value={walletAddress} 
                  size={200}
                  level="M"
                  includeMargin={true}
                  fgColor="#000000"
                  bgColor="#FFFFFF"
                />
              </div>
              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2">
                <button
                  onClick={handleCopyImage}
                  className={`
                    flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 shadow-lg
                    ${imageCopied 
                      ? 'bg-emerald-500 text-white' 
                      : 'bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white border border-slate-600'
                    }
                  `}
                >
                  {imageCopied ? <FiCheck size={14} /> : <FiDownload size={14} />}
                  <span>{imageCopied ? t('deposit.copiedImage') : t('deposit.saveQr')}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="mb-6">
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block text-center">
              {t('deposit.depositAddress')}
            </label>
            <div 
              onClick={handleCopy}
              className="group flex items-center gap-3 p-3 bg-slate-950/60 border border-slate-700/60 rounded-xl hover:border-sky-500/40 hover:bg-slate-900/80 transition-all cursor-pointer"
            >
              <code className="flex-1 text-sm font-mono text-slate-300 text-center group-hover:text-sky-200 transition-colors break-all">
                {walletAddress}
              </code>
              <div className={`shrink-0 p-2 rounded-lg transition-colors ${copied ? 'text-emerald-400 bg-emerald-500/10' : 'text-slate-500 group-hover:text-sky-400 group-hover:bg-sky-500/10'}`}>
                {copied ? <FiCheck size={18} /> : <FiCopy size={18} />}
              </div>
            </div>
          </div>

          {/* Token Info */}
          <div className={`grid ${isSafe ? 'grid-cols-1' : 'grid-cols-2'} gap-3`}>
            <div className="p-4 rounded-xl bg-slate-800/40 border border-slate-700/50 text-center">
              <p className="text-xs text-slate-500 mb-1">{t('deposit.tradingFunds')}</p>
              <p className="font-bold text-emerald-400">USDC.e / USDC</p>
            </div>
            {!isSafe && (
              <div className="p-4 rounded-xl bg-slate-800/40 border border-slate-700/50 text-center">
                <p className="text-xs text-slate-500 mb-1">{t('deposit.gasFee')}</p>
                <p className="font-bold text-indigo-400">POL (Matic)</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
